import React, { useMemo } from "react";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { LmsLoanConditionsTable } from "../ui/LmsSharedTables";
import { GlobalConstants } from "../../constants/GlobalConstants";

export const loanConditionFields = (myForm, loanId?) => {
  if (loanId) {
    myForm.addCenteredGroup([myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "organizationContactId", disabled: true })]);
    myForm.addCenteredGroup([myForm.getDisplayField("Application #", { name: "applicationNumber" })]);
    myForm.addCenteredGroup([myForm.getDisplayField("Loan", { name: "loan" })]);
  }
  else {
    myForm.addCenteredGroup([myForm.getDropdownField("Loan", "loans/list-loans", { required: true })]);
  }

  myForm.addCenteredGroup([myForm.getDropdownField("Status", "metadata/list-condition-statuses", {
    name: "conditionStatusId",
    required: true,
    tableName: "conditionStatus",
    tableValueName: "conditionStatusId"
  })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Timing", "metadata/list-condition-timings", {
    name: "conditionTimingId",
    required: true,
    tableName: "conditionTiming",
    tableValueName: "conditionTimingId",
    isDisabledOnFunc: ({ inputs }) => ["Loan Review", "Fund Polling", "BTB P2 Loan Review"].includes(inputs.source || "")
  })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Condition Phase", "metadata/list-loan-condition-phases", {
    name: "loanConditionPhaseId",
    tableName: "loanConditionPhase",
    tableValueName: "loanConditionPhaseId"
  })]);
  myForm.addCenteredGroup([myForm.getInputField("Source", { disabled: true })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Use as Escrow Instruction", { name: "escrowInstruction", inlineEdit: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Condition", { name: "description", required: true, inlineEdit: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Reason", { inlineEdit: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("External Notes", { inlineEdit: true })]);
  return myForm;
};

export const AddLoanConditionsForm = ({ conditionSource = "Loan", loanId = "" }) => {
  const [{ data: loanDetailsData = undefined as any, isLoading }] = useFetchData(loanId ? `loans/${loanId}` : "");
  const values = loanId ? { loanId } : {};

  const initialValues = useMemo(() => {
     if (loanDetailsData) {
       const { applicationNumberDisplay, loanNumberDisplay, borrowerOrganizationId } = loanDetailsData;
       return {
         source: conditionSource,
         conditionStatusId: GlobalConstants.LOAN_CONDITION_STATUSES.OPEN,
         applicationNumber: applicationNumberDisplay,
         organizationContactId: borrowerOrganizationId,
         loan: loanNumberDisplay
       };
     }

     return {
       source: conditionSource,
       conditionStatusId: GlobalConstants.LOAN_CONDITION_STATUSES.OPEN,
     };
  }, [loanDetailsData, loanId]);

  const myForm = loanConditionFields(new BCFormController(initialValues, {
    values,
    submitSuffix: "loandetails/conditions",
    clearSuffixesOnSubmit: [`loans/${loanId}`],
    excludeFromSubmit: ["loan", "applicationNumber", "organizationContactId"]
  }), loanId);

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} />;
};

const EditLoanConditionsForm = ({ id }) => {
  const getConditionSuffix = `loandetails/condition/${id}`;
  const [{ data = {}, isLoading, error }] = useFetchData(getConditionSuffix);
  const loanId = data?.loanId || "";
  const [{ data: loanData = {}, isLoading: isLoanLoading }] = useFetchData(
    loanId ? `loans/${loanId}` : ""
  );

  const { applicationNumberDisplay, loanNumberDisplay, borrowerOrganizationId } = loanData;
  const isShow = (data.description || "").toLowerCase() === "enter a note date";

  const initialValues = {
    ...data,
    applicationNumber: applicationNumberDisplay,
    loan: loanNumberDisplay,
    organizationContactId: borrowerOrganizationId,
  };

  const myForm = loanConditionFields(
    new BCFormController(initialValues, {
      values: { loanId, source: data.source },
      submitSuffix: "loandetails/conditions",
      deleteSuffix: "loandetails/conditions",
      clearSuffixesOnSubmit: [getConditionSuffix],
      initIsDisabled: isShow,
    }),
    true
  );

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading || isLoanLoading}
      error={error}
      actions={
        isShow
          ? ["close"]
          : ["save", "save-close", "delete", "close"]
      }
    />
  );
};

const LmsServicingLoanConditions = ({ params = {}, history }) => {
  const { isMainPage, isAdd, isEdit, isActionAllowed, id, query } = useParseRouteActions(params);
  const loanId = query.get("loanId") || "";
  const conditionSource = query.get("conditionSource") || "Loan";
  const mainTableSuffix = "loandetails/conditions";
  if (!isActionAllowed) {
    history.goBack();
  }

  return (<>
    {isMainPage && <LmsLoanConditionsTable suffix={mainTableSuffix} useApiFiltering />}
    {isAdd && <AddLoanConditionsForm conditionSource={conditionSource} loanId={loanId} />}
    {isEdit && <EditLoanConditionsForm id={id} />}
  </>);
};

export default LmsServicingLoanConditions;